// Thin, typed wrapper around supabase-js auth methods that maps every error
// to a stable, UI-translatable code. Pages never read `error.message` from
// supabase directly — they switch on `AuthErrorCode`.

import type { AuthError } from '@supabase/supabase-js';

import { env } from './env';
import { getSupabaseClient, type DdmSupabaseClient } from './supabase';

export type AuthErrorCode =
  | 'invalid_credentials'
  | 'rate_limited'
  | 'user_not_found'
  | 'email_already_used'
  | 'email_not_verified'
  | 'token_invalid'
  | 'weak_password'
  | 'network'
  // OAuth: thrown by AuthCallbackPage when a session lands on an existing
  // `auth_provider='email'` account. D1 belt-and-braces — Supabase Auth's
  // "link email identities = OFF" should refuse upstream; this is a
  // post-callback safety net.
  | 'oauth_email_taken'
  // OAuth: provider responded but the resulting identity could not be
  // linked to a usable account (Supabase rejects the callback). Rare in
  // production — Supabase config typically prevents this upstream.
  | 'oauth_account_not_linked'
  // OAuth: the provider identity is already linked to a different user
  // record. Surfaced when Supabase Auth refuses to overwrite an existing
  // link.
  | 'provider_account_already_linked'
  // OAuth: the user denied consent on the provider screen and Supabase
  // returned the cancellation as an error code rather than a hash redirect.
  | 'consent_denied'
  // M5 sub-lot 3/3: reCAPTCHA verdict from the Edge Function. The score is
  // never exposed to the browser — the page only knows pass / fail / 429.
  | 'recaptcha_failed'
  | 'recaptcha_rate_limited'
  | 'generic';

export class AuthActionError extends Error {
  readonly code: AuthErrorCode;
  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AuthActionError';
    this.code = code;
  }
}

export type SignUpInput = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
};

export type SignInInput = {
  email: string;
  password: string;
};

const verifyRedirectUrl = () => `${env.appUrl}/verify/success`;
const resetRedirectUrl = () => `${env.appUrl}/reset-password/confirm`;
// OAuth callback must land on the origin that initiated the flow (prod /
// branch / deploy-preview / localhost). Reading window.location.origin at
// runtime avoids hard-coding env.appUrl, which is build-time and would be
// the same fallback string on every Netlify build.
const oauthCallbackRedirectUrl = () =>
  `${typeof window !== 'undefined' ? window.location.origin : env.appUrl}/auth/callback`;

// Supabase Auth API ≥ 2024-01-01 exposes a stable `error_code` on the body
// and an `x-sb-error-code` header; the JS SDK surfaces it as `error.code`.
// Matching this is more robust than regex on `message`, which gets retranslated
// across SDK versions. Regex fallbacks remain for older payloads and edge fns.
const STRUCTURED_CODE_MAP: Readonly<Record<string, AuthErrorCode>> = {
  invalid_credentials: 'invalid_credentials',
  email_not_confirmed: 'email_not_verified',
  user_not_found: 'user_not_found',
  user_already_exists: 'email_already_used',
  email_exists: 'email_already_used',
  weak_password: 'weak_password',
  over_request_rate_limit: 'rate_limited',
  over_email_send_rate_limit: 'rate_limited',
  over_sms_send_rate_limit: 'rate_limited',
  otp_expired: 'token_invalid',
  otp_disabled: 'token_invalid',
  bad_jwt: 'token_invalid',
  flow_state_expired: 'token_invalid',
  flow_state_not_found: 'token_invalid',
  // OAuth Supabase Auth codes (>= 2024). Each variant Supabase has surfaced
  // in the wild maps to one of three stable application codes that pages
  // and toasts can rely on.
  oauth_account_not_linked: 'oauth_account_not_linked',
  identity_not_found: 'oauth_account_not_linked',
  provider_account_already_linked: 'provider_account_already_linked',
  identity_already_exists: 'provider_account_already_linked',
  oauth_callback_provider_denied: 'consent_denied',
  access_denied: 'consent_denied',
  user_cancelled_authorization: 'consent_denied',
};

function mapError(err: AuthError | Error | unknown): AuthActionError {
  if (err instanceof AuthActionError) return err;
  if (!err || typeof err !== 'object') return new AuthActionError('generic');

  const message = (err as { message?: string }).message?.toLowerCase() ?? '';
  const status = (err as { status?: number }).status;
  const code = (err as { code?: string }).code;

  if (code && code in STRUCTURED_CODE_MAP) {
    return new AuthActionError(STRUCTURED_CODE_MAP[code]!, message || code);
  }

  if (status === 429 || /rate limit|too many/.test(message)) {
    return new AuthActionError('rate_limited', message);
  }
  if (/invalid login credentials|invalid email or password/.test(message)) {
    return new AuthActionError('invalid_credentials', message);
  }
  if (/email not confirmed|email_not_confirmed/.test(message)) {
    return new AuthActionError('email_not_verified', message);
  }
  if (/already registered|already exists|user already/.test(message)) {
    return new AuthActionError('email_already_used', message);
  }
  if (/user not found|no user/.test(message)) {
    return new AuthActionError('user_not_found', message);
  }
  if (/token|otp|expired/.test(message)) {
    return new AuthActionError('token_invalid', message);
  }
  if (/password.*(weak|short|strength)/.test(message)) {
    return new AuthActionError('weak_password', message);
  }
  if (/account.*not.*linked|identity.*not.*found/.test(message)) {
    return new AuthActionError('oauth_account_not_linked', message);
  }
  if (/already.*linked|identity.*already/.test(message)) {
    return new AuthActionError('provider_account_already_linked', message);
  }
  if (/access.denied|consent.*denied|user.*cancel(led)? authorization/.test(message)) {
    return new AuthActionError('consent_denied', message);
  }
  if (/fetch|network|failed to fetch/.test(message)) {
    return new AuthActionError('network', message);
  }
  return new AuthActionError('generic', message);
}

export async function signUp(
  input: SignUpInput,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      emailRedirectTo: verifyRedirectUrl(),
      data: {
        first_name: input.firstName,
        last_name: input.lastName,
        phone: input.phone ?? null,
      },
    },
  });
  if (error) throw mapError(error);
}

export async function signIn(
  input: SignInInput,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });
  if (error) throw mapError(error);
}

export async function sendPasswordReset(
  email: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.resetPasswordForEmail(email, {
    redirectTo: resetRedirectUrl(),
  });
  if (error) throw mapError(error);
}

export async function updatePassword(
  newPassword: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.updateUser({ password: newPassword });
  if (error) throw mapError(error);
}

export async function resendVerification(
  email: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: verifyRedirectUrl() },
  });
  if (error) throw mapError(error);
}

export async function signOut(client: DdmSupabaseClient = getSupabaseClient()): Promise<void> {
  const { error } = await client.auth.signOut();
  if (error) throw mapError(error);
}

// Kicks off the Supabase Auth Google OAuth flow. The supabase-js SDK
// performs the redirect itself (window.location → Google consent screen),
// so this function never resolves on the success path — the page navigates
// away. Errors thrown before the redirect (provider disabled,
// redirectTo not whitelisted) come back through `error` and are mapped
// to AuthErrorCode like the rest of this module.
//
// Post-redirect handling (collision with an existing email/password
// account, role-based dashboard routing, signin_oauth audit log) lives
// in AuthCallbackPage — see src/routes/auth/AuthCallbackPage.tsx.
export async function signInWithGoogle(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: oauthCallbackRedirectUrl(),
    },
  });
  if (error) throw mapError(error);
}

// Exposed for tests.
export const __mapErrorForTests = mapError;
